<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import SpinWheelService, { type SpinWheelPlacement } from '@/services/SpinWheelService'
import SpinWheelModal from './SpinWheelModal.vue'
import type { WheelSegment } from './SpinWheel.vue'

const props = defineProps<{ page: 'home' | 'product' | 'account'; productId?: number | null }>()
const userStore = useUserStore()
const placement = ref<SpinWheelPlacement | null>(null)
const open = ref(false)
const segments = computed<WheelSegment[]>(() =>
  (placement.value?.labels || []).map((label, index) => ({
    label,
    tone: (['forest', 'ivory', 'pine', 'sand'] as const)[index % 4]
  }))
)
let timer: number | undefined
let requestNumber = 0

function clearTrigger() {
  window.clearTimeout(timer)
  window.removeEventListener('scroll', onScroll)
}

function onScroll() {
  if (!placement.value) return
  const scrollable = document.documentElement.scrollHeight - window.innerHeight
  const percent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0
  if (percent >= placement.value.spin_scroll_time) show()
}

function storageKey(moduleId: number) {
  return `spin-wheel-popup:${userStore.user.customerId || 'guest'}:${moduleId}`
}

function show() {
  if (!placement.value) return
  clearTrigger()
  open.value = true
  const days = Math.max(0, placement.value.display_interval)
  if (days) {
    localStorage.setItem(storageKey(placement.value.module_id), String(Date.now() + days * 86400000))
  }
}

watch(
  () => [userStore.user.customerId, props.page, props.productId] as const,
  async ([customerId, page, productId]) => {
    const currentRequest = ++requestNumber
    clearTrigger()
    open.value = false
    placement.value = null
    if (page === 'product' && !productId) return
    try {
      const result = await SpinWheelService.placement(page, productId ?? undefined)
      if (currentRequest !== requestNumber || !result?.labels?.length) return
      if (result.display_interval > 0) {
        const savedUntil = Number(localStorage.getItem(storageKey(result.module_id)) || 0)
        if (savedUntil > Date.now()) return
      }
      placement.value = result
      if (result.when_to_display === 2) {
        timer = window.setTimeout(show, Math.max(0, result.spin_popup_time) * 1000)
      } else if (result.when_to_display === 3) {
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
      } else {
        show()
      }
    } catch {
      // The promotion must not block its host page.
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  requestNumber++
  clearTrigger()
})
</script>

<template>
  <SpinWheelModal
    v-if="placement"
    :open="open"
    :segments="segments"
    :module-id="String(placement.module_id)"
    :logged-in="!!userStore.user.customerId"
    :account-email="userStore.user.email || ''"
    :account-first-name="userStore.user.firstName || ''"
    :account-last-name="userStore.user.lastName || ''"
    @close="open = false"
    @request-login="open = false; userStore.requestLogin()"
  />
</template>
